import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Modal,
  Select,
  Textarea,
  Table,
  Checkbox,
  Badge,
  Button,
  Group,
  Text,
  Stack,
  Alert,
  Skeleton,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { licensesService } from '../../services/api/licenses';
import { useActionMutation } from '../../hooks';
import { LICENSE_STATUS_COLORS } from '../../constants/colors';
import type { Customer, License, BulkTargetStatus, BulkStatusUpdateInput, LicenseStatus } from '../../types';

interface BulkActionsModalProps {
  opened: boolean;
  onClose: () => void;
  customer: Customer | null;
}

const TARGET_STATUS_OPTIONS = [
  { value: 'suspended', label: 'Suspended' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'active', label: 'Active (Reactivate)' },
];

function canTransition(currentStatus: LicenseStatus, targetStatus: BulkTargetStatus): boolean {
  if (currentStatus === targetStatus) return false;
  if (currentStatus === 'revoked') return false;
  if (currentStatus === 'expired') return false;
  if (targetStatus === 'active' && currentStatus !== 'suspended') return false;
  return true;
}

export function BulkActionsModal({ opened, onClose, customer }: BulkActionsModalProps) {
  const [targetStatus, setTargetStatus] = useState<BulkTargetStatus | null>(null);
  const [reason, setReason] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: licensesData, isLoading } = useQuery({
    queryKey: ['customer-licenses-bulk', customer?.id],
    queryFn: () => licensesService.search({ customerId: customer!.id, limit: 1000 }),
    enabled: opened && !!customer,
  });

  const allLicenses: License[] = useMemo(
    () => (licensesData?.licenses ?? []) as License[],
    [licensesData]
  );

  const eligibleLicenses = useMemo(() => {
    if (!targetStatus) return [];
    return allLicenses.filter(l => canTransition(l.status, targetStatus));
  }, [allLicenses, targetStatus]);

  // Reset selection when target status or modal state changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [targetStatus]);

  useEffect(() => {
    if (!opened) {
      setTargetStatus(null);
      setReason('');
      setSelectedIds(new Set());
    }
  }, [opened]);

  const allSelected = eligibleLicenses.length > 0 && selectedIds.size === eligibleLicenses.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligibleLicenses.map(l => l.id)));
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const bulkMutation = useActionMutation<unknown, BulkStatusUpdateInput>(
    (input) => licensesService.bulkUpdateStatus(input),
    {
      queryKeys: ['customers', 'customer-licenses-bulk', 'licenses'],
      successMessage: `Bulk status update completed`,
      onSuccessCallback: onClose,
    }
  );

  const handleConfirm = () => {
    if (!targetStatus || !reason.trim() || selectedIds.size === 0) return;
    bulkMutation.mutate({
      licenseIds: Array.from(selectedIds),
      targetStatus,
      reason: reason.trim(),
    });
  };

  const canSubmit = !!targetStatus && reason.trim().length > 0 && selectedIds.size > 0 && !bulkMutation.isPending;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Bulk Actions — ${customer?.name ?? ''}`}
      size="lg"
    >
      <Stack gap="md">
        <Select
          label="Target Status"
          placeholder="Select target status"
          data={TARGET_STATUS_OPTIONS}
          value={targetStatus}
          onChange={(v) => setTargetStatus(v as BulkTargetStatus | null)}
        />

        <Textarea
          label="Reason"
          placeholder="Reason for status change (required)"
          required
          minRows={2}
          maxLength={1000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        {targetStatus && (
          <>
            {isLoading ? (
              <Stack gap="xs">
                {[1, 2, 3].map(i => <Skeleton key={i} height={40} />)}
              </Stack>
            ) : eligibleLicenses.length === 0 ? (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow">
                No licenses are eligible for this status change.
              </Alert>
            ) : (
              <>
                <Text size="sm" c="dimmed">
                  {eligibleLicenses.length} eligible license{eligibleLicenses.length !== 1 ? 's' : ''} — {selectedIds.size} selected
                </Text>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={40}>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={selectedIds.size > 0 && !allSelected}
                          onChange={toggleAll}
                        />
                      </Table.Th>
                      <Table.Th>EID</Table.Th>
                      <Table.Th>Current Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {eligibleLicenses.map(license => (
                      <Table.Tr key={license.id}>
                        <Table.Td>
                          <Checkbox
                            checked={selectedIds.has(license.id)}
                            onChange={() => toggleOne(license.id)}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>{license.eid}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color={LICENSE_STATUS_COLORS[license.status]} variant="light">
                            {license.status}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            )}
          </>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={!canSubmit}
            loading={bulkMutation.isPending}
            color={targetStatus === 'revoked' ? 'red' : targetStatus === 'suspended' ? 'orange' : 'green'}
          >
            Confirm Update ({selectedIds.size})
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
